import React, { useState, useEffect, useRef } from 'react';
import { message } from 'antd';
import { 
  PlusIcon,
  UserGroupIcon,
  TrashIcon
} from '@heroicons/react/outline';
import styled from 'styled-components';
import { channelService } from '../../services/channelService';
import { Channel } from './types';
import { OutlookIcon, GmailIcon } from '../../design-system';
import {
  Button,
  LoadingState,
  Input,
  colors,
  typography,
  spacing,
  transitions,
} from '../../design-system';
import { Card, Tag, Dropdown } from 'antd';
import type { MenuProps } from 'antd';

// Import OAuth components from index
import { OutlookOAuthButton, GmailOAuthButton } from './OAuth';

interface ChannelsSectionProps {
  // No props needed - single user admin system
}

interface ChannelCardProps {
  channel: Channel;
  effectiveIsAdmin: boolean;
  onChannelDeleted: () => void;
}

const SectionTitle = styled.h3`
  margin: 0 0 ${spacing[2]};
  font-size: ${typography.fontSizes.sm};
  font-weight: ${typography.fontWeights.normal};
  color: ${colors.text.secondary};
`;

const ChannelsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: ${spacing[4]};
  margin-top: ${spacing[2]};
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: ${spacing[6]};
  color: ${colors.text.secondary};
  font-size: ${typography.fontSizes.sm};
`;

const StaticCard = styled.div`
  transition: ${transitions.all};
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
`;

// ChannelCard component (merged inline)
const ChannelCard: React.FC<ChannelCardProps> = ({
  channel,
  effectiveIsAdmin,
  onChannelDeleted
}) => {
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deleting, setDeleting] = useState(false);

  const getChannelIcon = (type: string, subtype?: string) => {
    // Debug: Log what we're receiving
    console.log('getChannelIcon called with:', { type, subtype });
    if (type === 'email') {
      // Check for Gmail (case-insensitive)
      if (subtype && subtype.toLowerCase() === 'gmail') {
        console.log('Returning Gmail icon');
        return <GmailIcon size={32} />;
      }
      // Default to Outlook for email channels
      console.log('Returning Outlook icon (default for email)');
      return <OutlookIcon size={32} />;
    }
    return <OutlookIcon size={32} />;
  };

  const getChannelTypeLabel = (type: string) => {
    switch (type) {
      case 'email':
        return 'Email Channel';
      default:
        return 'Channel';
    }
  };

  const isPersonalChannel = channel.ownership_type === 'personal';
  const isSharedChannel = !isPersonalChannel;
  const canRemove = channel.ownership_type === 'personal' || effectiveIsAdmin;

  const handleDeleteChannel = async () => {
    setDeleting(true);
    try {
      const { channelService } = await import('../../services/channelService');
      if (channel.channel_type === 'email') {
        const subtype = channel.channel_subtype;
        if (subtype === 'outlook') {
          await channelService.disconnectOutlook();
        } else if (subtype === 'gmail') {
          await channelService.disconnectGmail();
        } else {
          await channelService.removeChannel(channel.id);
        }
      } else {
        await channelService.removeChannel(channel.id);
      }
      // Show success message
      const { message } = await import('antd');
      // message.success(`${channel.identifier} channel removed successfully`);
      onChannelDeleted();
    } catch (error: any) {
      console.error('Error removing channel:', error);
      const { message } = await import('antd');
      const errorMessage = error?.response?.data?.error || error?.message || 'Failed to remove channel';
      // message.error(errorMessage);
      setDeleting(false);
    }
  };

  const isDeleteEnabled = deleteConfirmation === channel.identifier;

  return (
    <div style={{ position: 'relative' }}>
      {/* Team badge for shared channels */}
      {isSharedChannel && (
        <div style={{
          position: 'absolute',
          top: '-8px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 2
        }}>
          <Tag color="success">Team</Tag>
        </div>
      )}

      <StaticCard>
        <Card style={{ padding: spacing[6] }}>
          {/* Channel Content */}
          <div style={{
            display: 'flex', 
            alignItems: 'center', 
            gap: spacing[4],
            marginBottom: spacing[4]
          }}>
            {/* Icon with team indicator for shared channels */}
            <div style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {getChannelIcon(channel.channel_type, channel.channel_subtype)}
              {isSharedChannel && (
                <div style={{
                  position: 'absolute',
                  top: '-4px',
                  right: '-4px',
                  backgroundColor: colors.feedback.success.primary,
                  borderRadius: '50%',
                  width: '16px',
                  height: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <UserGroupIcon style={{ width: '10px', height: '10px', color: 'white' }} />
                </div>
              )}
            </div>

            {/* Channel info */}
            <div style={{ flex: 1 }}>
              <div style={{ 
                fontSize: typography.fontSizes.lg, 
                fontWeight: typography.fontWeights.semibold, 
                color: colors.text.primary,
                marginBottom: spacing[1]
              }}>
                {channel.identifier}
              </div>
              <div style={{ 
                fontSize: typography.fontSizes.sm, 
                color: colors.text.secondary,
                display: 'flex',
                alignItems: 'center',
                gap: spacing[2]
              }}>
                <span>{getChannelTypeLabel(channel.channel_type)}</span>
                <span>•</span>
                <span>{channel.ownership_type === 'personal' ? 'Personal' : 'Shared'}</span>
              </div>
            </div>
          </div>

          {/* Channel Details */}
          <div style={{ 
            padding: spacing[3],
            backgroundColor: colors.background.secondary,
            borderRadius: '6px',
            border: `1px solid ${colors.border.secondary}`,
            marginBottom: spacing[4]
          }}>
            <div style={{ 
              fontSize: typography.fontSizes.sm,
              color: colors.text.secondary,
              marginBottom: spacing[2]
            }}>
              <strong>Type:</strong> {getChannelTypeLabel(channel.channel_type)}
            </div>
            <div style={{ 
              fontSize: typography.fontSizes.sm,
              color: colors.text.secondary,
              marginBottom: spacing[2]
            }}>
              <strong>Ownership:</strong> {channel.ownership_type === 'personal' ? 'Personal' : 'Shared'}
            </div>
            <div style={{ 
              fontSize: typography.fontSizes.sm,
              color: colors.text.secondary
            }}>
              <strong>Identifier:</strong> {channel.identifier}
            </div>
          </div>

          {/* Delete Channel - Only show if user can remove */}
          {canRemove && (
            <div style={{ paddingTop: spacing[2], borderTop: `1px solid ${colors.border.secondary}` }}>
              <div style={{ 
                fontSize: typography.fontSizes.xs,
                color: colors.text.tertiary,
                marginBottom: spacing[2]
              }}>
                Danger Zone
              </div>
              
              <details>
                <summary style={{ 
                  fontSize: typography.fontSizes.xs,
                  color: colors.feedback.error.primary,
                  cursor: 'pointer',
                  marginBottom: spacing[2]
                }}>
                  Remove Channel
                </summary>
                
                <div style={{ marginLeft: spacing[2] }}>
                  <div style={{ 
                    fontSize: typography.fontSizes.xs,
                    color: colors.text.secondary,
                    marginBottom: spacing[2]
                  }}>
                    Type "{channel.identifier}" to confirm:
                  </div>
                  
                  <div style={{ display: 'flex', gap: spacing[2], alignItems: 'center' }}>
                    <Input
                      label=""
                      value={deleteConfirmation}
                      onChange={(e) => setDeleteConfirmation(e.target.value)}
                      placeholder={channel.identifier}
                      style={{ fontSize: typography.fontSizes.xs }}
                    />
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={<TrashIcon style={{ width: '12px', height: '12px' }} />}
                      onClick={handleDeleteChannel}
                      loading={deleting}
                      disabled={!isDeleteEnabled}
                      style={{ color: colors.feedback.error.primary }}
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              </details>
            </div>
          )}
        </Card>
      </StaticCard>
    </div>
  );
};

export const ChannelsSection: React.FC<ChannelsSectionProps> = () => {
  const [channels, setChannels] = useState<Channel[]>([]);
  const [loading, setLoading] = useState(true);
  const [creatingChannel, setCreatingChannel] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [channelType, setChannelType] = useState<'email' | ''>('');
  const [channelSubtype, setChannelSubtype] = useState<'outlook' | ''>('');
  
  // OAuth refs
  const outlookOAuthRef = useRef<HTMLDivElement>(null);
  const sharedOAuthRef = useRef<HTMLDivElement>(null);
  const gmailOAuthRef = useRef<HTMLDivElement>(null);
  const gmailSharedOAuthRef = useRef<HTMLDivElement>(null);

  const effectiveIsAdmin = true; // Always true for single-user admin

  useEffect(() => {
    loadChannels();
  }, []);

  const loadChannels = async () => {
    setLoading(true);
    try {
      const channelsData = await channelService.getAllChannels();
      // Debug: Log channel data to verify subtype is set correctly
      console.log('Loaded channels:', channelsData.map((c: Channel) => ({
        id: c.id,
        type: c.channel_type,
        subtype: c.channel_subtype,
        identifier: c.identifier
      })));
      setChannels(channelsData);
    } catch (error) {
      console.error('Error loading channels:', error);
      // message.error('Failed to load channels');
    } finally {
      setLoading(false);
    }
  };

  const handleChannelsUpdated = () => {
    loadChannels();
  };

  const handleChannelDeleted = () => {
    loadChannels();
  };

  const handleConnectPersonalEmail = () => {
    if (outlookOAuthRef.current) {
      const button = outlookOAuthRef.current.querySelector('button');
      if (button) {
        button.click();
      }
    }
  };

  const handleConnectSharedEmail = () => {
    if (effectiveIsAdmin && sharedOAuthRef.current) {
      const button = sharedOAuthRef.current.querySelector('button');
      if (button) {
        button.click();
      }
    }
  };

  const handleConnectPersonalGmail = () => {
    if (gmailOAuthRef.current) {
      const button = gmailOAuthRef.current.querySelector('button');
      if (button) {
        button.click();
      }
    }
  };

  const handleConnectSharedGmail = () => {
    if (effectiveIsAdmin && gmailSharedOAuthRef.current) {
      const button = gmailSharedOAuthRef.current.querySelector('button');
      if (button) {
        button.click();
      }
    }
  };


  const getAddChannelItems = (): MenuProps['items'] => {
    const items: MenuProps['items'] = [
      {
        key: 'email-group',
        label: 'Email',
        type: 'group',
      },
      {
        key: 'personal-outlook',
        label: 'Personal Outlook',
        onClick: handleConnectPersonalEmail
      },
      {
        key: 'personal-gmail',
        label: 'Personal Gmail',
        onClick: handleConnectPersonalGmail
      }
    ];

    return items;
  };

  if (loading) {
    return <LoadingState />;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing[2] }}>
        <SectionTitle>Connected Channels</SectionTitle>
        
        <Dropdown
          menu={{ items: getAddChannelItems() }}
          placement="bottom"
        >
          <Button
            variant="ghost"
            size="sm"
            icon={<PlusIcon style={{ width: '12px', height: '12px' }} />}
          >
            Add
          </Button>
        </Dropdown>
      </div>

      {channels.length === 0 ? (
        <EmptyMessage>
          No channels connected yet. Use the cards in Settings to connect your first channel.
        </EmptyMessage>
      ) : (
        <ChannelsGrid>
          {channels.map((channel) => (
            <ChannelCard
              key={channel.id}
              channel={channel}
              effectiveIsAdmin={effectiveIsAdmin}
              onChannelDeleted={handleChannelDeleted}
            />
          ))}
        </ChannelsGrid>
      )}

      {/* Hidden OAuth buttons */}
      <div style={{ display: 'none' }}>
        <div ref={outlookOAuthRef}>
          <OutlookOAuthButton onSuccess={handleChannelsUpdated} ownershipType="personal" />
        </div>
        <div ref={sharedOAuthRef}>
          <OutlookOAuthButton onSuccess={handleChannelsUpdated} ownershipType="shared" />
        </div>
        <div ref={gmailOAuthRef}>
          <GmailOAuthButton onSuccess={handleChannelsUpdated} ownershipType="personal" />
        </div>
        <div ref={gmailSharedOAuthRef}>
          <GmailOAuthButton onSuccess={handleChannelsUpdated} ownershipType="shared" />
        </div>
      </div>
    </div>
  );
};

export default ChannelsSection; 